import { useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { FeatureFlags, MeResponse } from '@ucp/web/lib';
import { landingRouteForUser } from './landingRoute';

interface LandingRedirectProps {
  readonly user: MeResponse;
  /**
   * Whether this component acts at all. Composed from several flags by the shell, so
   * it is NOT a semantic input and must never be forwarded to the landing resolver.
   */
  readonly enabled: boolean;
  /** The real Insights flag — passed through untouched, never inferred from `enabled`. */
  readonly insightsEnabled: boolean;
  readonly flags?: Partial<FeatureFlags>;
}

/**
 * MF-01 (PRO-0600 Increment 2 / ADR-014 A8) — the post-auth "front door" redirect,
 * WITHOUT re-rooting `/`. Rendered inside the router; fires at most once per shell
 * mount, on the initial render only, and only when the browser is at `/`.
 *
 * Semantics (SF-003):
 * - A `useRef` "has-evaluated" guard flips on the first render so the redirect is
 *   never re-evaluated for the lifetime of this mount — later client navigations back
 *   to `/` (e.g. clicking Communications/Home) do NOT re-trigger it, keeping Comms
 *   Home reachable.
 * - Uses `Navigate ... replace` so the redirect adds NO history entry — Back cannot
 *   bounce the user between `/` and the landing target (no loop).
 * - When `enabled` is false, or the caller is not at `/`, or the computed target is
 *   `/`, it renders `null` and performs no navigation. Behaviour-only (renders `null`
 *   in every case).
 *
 * PRO-1360 — this is the SINGLE owner of the landing redirect. The `/` route element
 * deliberately still renders `HomeView` unconditionally: a second `<Navigate>` there
 * would have no once-per-mount guard, so it would fire on every render at `/` and make
 * Comms Home permanently unreachable, discarding the property documented above.
 */
export function LandingRedirect({
  user,
  enabled,
  insightsEnabled,
  flags,
}: LandingRedirectProps) {
  const { pathname } = useLocation();
  const evaluated = useRef(false);

  // Only the initial mount at `/` can trigger a redirect; every later render is inert.
  if (evaluated.current || !enabled || pathname !== '/') {
    return null;
  }
  evaluated.current = true;

  const target = landingRouteForUser(user, insightsEnabled, flags);
  if (target === '/') {
    return null;
  }
  return <Navigate to={target} replace />;
}
